import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

/**
 * Single KPI card that renders a drill-through link.
 * `card` shape:
 *   { label, value, tone, hint, drill: {view, tab, status, ids, ...} }
 */
export function KpiCard({ card, testId }) {
  const tone = card?.tone || 'default';
  const drill = card?.drill || {};
  const href = _drillHref(drill);
  const border = {
    danger: 'border-[hsl(0_60%_78%)] bg-[hsl(0_90%_98%)]',
    warning: 'border-[hsl(38_80%_75%)] bg-[hsl(48_96%_97%)]',
    info: 'border-[hsl(210_70%_82%)] bg-[hsl(210_90%_98%)]',
    success: 'border-[hsl(142_60%_78%)] bg-[hsl(142_70%_97%)]',
    default: 'border-[hsl(var(--border))] bg-[hsl(var(--card))]',
  }[tone] || 'border-[hsl(var(--border))] bg-[hsl(var(--card))]';
  const valueTone = {
    danger: 'text-[hsl(0_60%_38%)]',
    warning: 'text-[hsl(38_60%_28%)]',
    info: 'text-[hsl(210_60%_30%)]',
    success: 'text-[hsl(142_50%_25%)]',
    default: 'text-[hsl(var(--foreground))]',
  }[tone] || '';

  const Body = (
    <div className={`border rounded-xl p-3 ${border} transition-shadow hover:shadow-sm h-full`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10.5px] uppercase tracking-wide text-[hsl(var(--foreground-muted))] truncate">
            {card.label}
          </div>
          <div className={`text-[22px] font-semibold tabular-nums mt-0.5 ${valueTone}`}>
            {card.value}
          </div>
        </div>
        {href && (
          <ChevronRight size={14} className="text-[hsl(var(--foreground-subtle))] mt-1 shrink-0" />
        )}
      </div>
      {card.hint && (
        <div className="text-[10.5px] text-[hsl(var(--foreground-muted))] mt-1 leading-snug">
          {card.hint}
        </div>
      )}
    </div>
  );

  if (!href) return <div data-testid={testId}>{Body}</div>;
  return <Link to={href} data-testid={testId} className="block">{Body}</Link>;
}

/**
 * KRA badge — numerator / denominator with a progress dot.
 * `kra` shape: { name, numerator, denominator, pct, hint }
 */
export function KraBar({ kra, testId }) {
  const den = kra.denominator || 0;
  const num = kra.numerator || 0;
  const pct = kra.pct;
  const width = pct == null ? 0 : Math.max(2, Math.min(100, pct));
  const tone = pct == null ? 'default' :
               pct >= 80 ? 'success' :
               pct >= 60 ? 'warning' : 'danger';
  const barBg = {
    success: 'bg-[hsl(142_60%_45%)]',
    warning: 'bg-[hsl(38_90%_55%)]',
    danger: 'bg-[hsl(0_70%_55%)]',
    default: 'bg-[hsl(var(--foreground-subtle))]',
  }[tone];

  return (
    <div className="border rounded-xl p-3 bg-[hsl(var(--card))]"
         style={{ borderColor: 'hsl(var(--border))' }}
         data-testid={testId}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="text-[13px] font-semibold truncate">{kra.name}</div>
        <div className="text-[13px] font-mono tabular-nums whitespace-nowrap">
          {num} <span className="text-[hsl(var(--foreground-subtle))]">/</span> {den || '—'}
          {pct != null && (
            <span className="text-[11px] text-[hsl(var(--foreground-muted))] ml-1.5">
              ({pct}%)
            </span>
          )}
        </div>
      </div>
      <div className="h-1.5 rounded-full bg-[hsl(var(--surface-1))] overflow-hidden">
        <div className={`h-full ${barBg} transition-all`} style={{ width: `${width}%` }} />
      </div>
      {kra.hint && (
        <div className="text-[10.5px] text-[hsl(var(--foreground-muted))] mt-1.5 leading-snug">
          {kra.hint}
        </div>
      )}
    </div>
  );
}

function _drillHref(drill) {
  if (!drill || !drill.view) return null;
  const q = new URLSearchParams();
  if (drill.tab) q.set('tab', drill.tab);
  if (drill.status) q.set('status', drill.status);
  if (drill.overdue) q.set('overdue', '1');
  if (drill.stale) q.set('stale', '1');
  if (drill.due) q.set('due', drill.due);
  if (drill.no_followup) q.set('no_followup', '1');
  if (drill.item_type) q.set('item_type', drill.item_type);
  if (drill.gap) q.set('gap', drill.gap);
  const qs = q.toString();
  if (drill.view === 'followups') return `/followups${qs ? '?' + qs : ''}`;
  if (drill.view === 'master_projects') return `/master-projects${qs ? '?' + qs : ''}`;
  if (drill.view === 'data_quality') return `/reports/data-quality${qs ? '?' + qs : ''}`;
  if (drill.view === 'team') return `/team${qs ? '?' + qs : ''}`;
  return null;
}
